import type { Theme } from '@src/app/themes';
import { useAuthentication } from '@src/features/auth';
import { formatTaskDate } from '@src/features/tasks/lib';
import {
  subscribeToTaskRealtimeEvents,
  useTasks,
} from '@src/features/tasks/model';
import { resolveTaskActionRoute } from '@src/features/tasks/route';
import type { Task } from '@src/features/tasks/types';
import { useInfiniteScrollSentinel } from '@src/shared/hooks';
import { Button } from '@src/shared/ui/Button';
import {
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
} from '@src/shared/ui/List';
import { Box, Stack } from '@src/shared/ui/layout';
import Tooltip from '@src/shared/ui/Tooltip';
import Typography from '@src/shared/ui/Typography';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import TaskTypeIcon from '../TaskTypeIcon';

const PAGE_SIZE = 25;

const StyleSheet = {
  list: {
    marginTop: '5px',
    mx: -2.5,
  },
  listItem: (theme: Theme) => ({
    padding: '6px 20px',
    borderBottom: '1px solid',
    borderColor: 'grey.200',
    gap: '6px',
    '& .MuiListItemText-root': {
      margin: 0,
    },
    '& .MuiListItemText-secondary': {
      color: theme.palette.grey[800],
    },
  }),
  taskIconWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  empty: {
    color: 'text.secondary',
    marginTop: '5px',
  },
  error: {
    alignItems: 'flex-start',
    marginTop: '5px',
  },
  scrollSentinel: {
    height: '1px',
  },
} as const;

type TasksListProps = {
  referenceType?: string;
};

export default function TasksList({ referenceType }: TasksListProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthentication();

  // The list the task page opens on, narrowed to one section. Kept in step
  // with `initialFilterState` there: a panel answering a different question
  // than the page it links to reads as one of the two being out of date.
  const filterParams = useMemo(
    () => ({
      task_status: 'pending',
      ...(user?.id ? { task_user: String(user.id) } : {}),
      ...(referenceType ? { task_reference_type: referenceType } : {}),
    }),
    [referenceType, user?.id],
  );

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isError,
    isFetchingNextPage,
    isPending,
    refetch,
  } = useTasks({ filterParams, pageSize: PAGE_SIZE });

  // Completing a task anywhere writes it into every cached task list, this one
  // included, preserving position rather than dropping rows that stopped
  // matching -- the task page needs the row it just ticked to stay put. Here
  // nothing is being edited, so a closed task is simply no longer an answer.
  const tasks = useMemo(
    () =>
      (data?.pages.flatMap((page) => page.results) ?? []).filter(
        (task) => task.status === 'pending',
      ),
    [data],
  );

  const scrollSentinelRef = useInfiniteScrollSentinel({
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  });

  // These tasks are raised by other people's actions, so without this the
  // panel would sit stale until something on this page caused a fetch.
  useEffect(() => {
    const unsubscribe = subscribeToTaskRealtimeEvents(() => void refetch());
    return () => {
      unsubscribe();
    };
  }, [refetch]);

  const handleOpenTask = useCallback(
    (task: Task) => {
      void navigate(resolveTaskActionRoute(task));
    },
    [navigate],
  );

  // Checked before the empty case: a request that gave up after its retries
  // also leaves nothing to show, and "no open tasks" would read as a cleared
  // queue rather than an answer that never arrived.
  if (isError && tasks.length === 0) {
    return (
      <Stack spacing={1} sx={StyleSheet.error}>
        <Typography variant="body2" color="text.secondary">
          {t('tasks.load_failed', 'Tasks could not be loaded.')}
        </Typography>
        <Button size="small" variant="outlined" onClick={() => void refetch()}>
          {t('common.try_again', 'Try again')}
        </Button>
      </Stack>
    );
  }

  if (!isPending && tasks.length === 0) {
    return (
      <Typography variant="body2" sx={StyleSheet.empty}>
        {t('tasks.no_open_tasks', 'No open tasks')}
      </Typography>
    );
  }

  return (
    <>
      <List disablePadding sx={StyleSheet.list}>
        {tasks.map((task) => {
          const content = (
            <>
              <ListItemAvatar>
                <Box sx={StyleSheet.taskIconWrapper}>
                  <TaskTypeIcon task={task} />
                </Box>
              </ListItemAvatar>
              <ListItemText
                primary={task.taskType.name}
                secondary={formatTaskDate(task.dueDate)}
                slotProps={{
                  primary: { variant: 'body2' },
                  secondary: { variant: 'body3' },
                }}
              />
            </>
          );

          // The row names the type and when it is due; what the task actually
          // asks for is the description, which is a sentence and too long to
          // sit in a panel this narrow. An empty title renders no tooltip at
          // all, which is what should happen for a task that has no
          // description rather than an empty box on hover.
          return (
            <Tooltip key={task.id} title={task.description || ''}>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => handleOpenTask(task)}
                  sx={StyleSheet.listItem}
                >
                  {content}
                </ListItemButton>
              </ListItem>
            </Tooltip>
          );
        })}
      </List>
      {hasNextPage && (
        <Box ref={scrollSentinelRef} sx={StyleSheet.scrollSentinel} />
      )}
    </>
  );
}
